import BlockScrollInternal from "@lhd/components/BlockScrollInternal";
import {
  APP_PART_TYPES,
  COLUMN_WIDTH,
  ROW_HEIGHT,
} from "@lhd/features/Apps/AppForm/constants";
import { AppPartType } from "@lhd/features/Apps/AppForm/types";
import { HIDDENS_COLUMNS_SHEET } from "@lhd/features/Apps/constants";
import {
  getLayoutWithMarginTop,
  getMinXAndMarginLeft,
  groupItemsByRow,
  groupItemsCol,
} from "@lhd/features/Apps/utils";
import { Spin } from "antd";
import { FC, useMemo } from "react";
import Title from "../components/Title";
import {
  HIDDEN_TITLE_APP_PART_TYPES,
  OPERATOR_LABEL_TEXTS,
} from "../constants";
import { renderField } from "../untils";
import useLayoutApp, { ReceivedProps } from "./hook";
import { Styles } from "./styled";
import BusinessTripReportsCalculator from "./BusinessTripReportsCalculator";
import { useMatch } from "react-router-dom";
import { ROUTES } from "@lhd/constants";

// A label's text is authored as HTML, so compare on the text it renders.
const getLabelPlainText = (item: AppPartType) =>
  String(item.label_text || item.name || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();

const LayoutApp = ({
  appParts,
  appPartsFlat,
  selectionDataList,
  isEditing,
  isLoading,
  isEditPage,
  isPDF,
  permission,
  onChange,
  appRecord,
  handleImportTableRows,
  allowSearchDate,
  form,
  appType,
  showPrintButton,
  onPrintPDF,
  isPrinting,
  isCreate,
  disabledInEditMode,
  isQuickViewMode,
}: ReturnType<typeof useLayoutApp>) => {
  const isDisabled = !isEditing || disabledInEditMode;
  const matchCreateBusinessTripReports = useMatch(
    `/${ROUTES.BUSINESS_TRIP_REPORTS}/:id/create`,
  );
  const isBusinessManuals = Boolean(useMatch(`/${ROUTES.BUSINESS_MANUALS}/*`));
  const newLayout = useMemo(() => {
    if (!appParts) return [];

    const layoutSorted = [...appParts].sort((a, b) => {
      return (
        (a.item_config_data?.h ?? 0) +
        (a.item_config_data?.y ?? 0) -
        ((b.item_config_data?.h ?? 0) + (b.item_config_data?.y ?? 0))
      );
    });

    const layoutItem = layoutSorted.map((item) => {
      const config = item.item_config_data ?? {};

      return {
        ...item,
        item_config_data: {
          ...config,
          yBottom: (config.h ?? 0) + (config.y ?? 0),
        },
      };
    });

    const newLayoutByRow = groupItemsByRow(layoutItem);
    const layoutWithMarginTop = getLayoutWithMarginTop(newLayoutByRow);

    const result = layoutWithMarginTop.map((x) => ({
      ...x,
      items: groupItemsCol(x.items),
    }));

    // Đưa nhóm chứa ステータス lên đầu tiên
    const statusIndex = result.findIndex((row) =>
      row.items.some((col) =>
        col.some((item: any) => item.name === "ステータス"),
      ),
    );

    if (statusIndex > 0) {
      const [statusRow] = result.splice(statusIndex, 1);
      result.unshift(statusRow);
    }

    return result;
  }, [appParts]);

  const getRenderedColumnWidthPx = (appPart: AppPartType) => {
    const label = appPart.label_text || appPart.name || "";
    const baseWidthPx = appPart.item_config_data.w * COLUMN_WIDTH;

    if (label === "章条項" || label === "章条项") {
      return Math.round((baseWidthPx * 2) / 3);
    }

    return baseWidthPx;
  };

  const targetXCenter = useMemo(() => {
    const table = appParts?.find(
      (it) =>
        it.label_text === "テーブル" ||
        it.name === "テーブル" ||
        it.field_code === "テーブル",
    );
    if (!table || !table.children) return null;

    // Filter out hidden columns consistently with the actual table rendering logic
    const dataCols = table.children.filter(
      (ap) => !HIDDENS_COLUMNS_SHEET.includes(ap.app_part_type),
    );
    const sortedCols = [...dataCols].sort(
      (a, b) => a.item_config_data.x - b.item_config_data.x,
    );

    // The index (STT) column at the start is 60px wide
    const sttWidthPx = 60;

    // Calculate total width of the next 6 data columns (to make a total of 7 columns including STT)
    const first6DataColsWidthPx = sortedCols.slice(0, 6).reduce((acc, cur) => {
      if (cur.app_part_type === APP_PART_TYPES.NUMBER) {
        return acc + 7 * COLUMN_WIDTH;
      }

      return acc + getRenderedColumnWidthPx(cur);
    }, 0);

    const tableX = table.item_config_data.x;
    return tableX * COLUMN_WIDTH + (sttWidthPx + first6DataColsWidthPx) / 2;
  }, [appParts]);

  const renderCol = (itemLayouts: {
    items: any[];
    maxBottomY: number;
    marginTop: number;
    minY: number;
  }) => {
    let preMaxRightX = 0;
    // Row bands taken by fields that do render a title. A label sits in its own
    // column group, so this has to look across the whole row, not just the
    // group the label belongs to.
    const titledBands = ([] as AppPartType[])
      .concat(...itemLayouts.items)
      .filter(
        (it: AppPartType) =>
          !HIDDEN_TITLE_APP_PART_TYPES.includes(
            it.app_part_type as typeof HIDDEN_TITLE_APP_PART_TYPES[number],
          ),
      )
      .map((it: AppPartType) => [
        it.item_config_data.y,
        it.item_config_data.y + it.item_config_data.h,
      ]);
    return itemLayouts.items.map((layoutCol, indexCol) => {
      const { maxRightX, minX } = getMinXAndMarginLeft(layoutCol);
      const marginRight = minX - preMaxRightX;
      preMaxRightX = maxRightX;
      const col = maxRightX - minX;

      const colMinY = Math.min(
        ...layoutCol.map((item: AppPartType) => item.item_config_data.y),
      );
      const colMaxBottomY = Math.max(
        ...layoutCol.map(
          (item: AppPartType) =>
            item.item_config_data.y + item.item_config_data.h,
        ),
      );
      const row = colMaxBottomY - colMinY;

      let gridTemplateRowsStr = `repeat(${row}, minmax(${
        isPDF ? ROW_HEIGHT * 0.6 : ROW_HEIGHT
      }px, auto))`;
      if (isPDF) {
        const coveredRows = new Set<number>();
        layoutCol.forEach((item: AppPartType) => {
          if (item.app_part_type === APP_PART_TYPES.TABLE) {
            const startRow = item.item_config_data.y - colMinY + 1;
            for (let i = 1; i < item.item_config_data.h; i++) {
              coveredRows.add(startRow + i);
            }
          }
        });

        if (coveredRows.size) {
          gridTemplateRowsStr = Array.from({ length: row }, (_, i) =>
            coveredRows.has(i + 1) ? "0px" : `minmax(${ROW_HEIGHT}px, auto)`,
          ).join(" ");
        }
      }

      return (
        <span key={indexCol}>
          <div
            style={{
              marginLeft: marginRight * COLUMN_WIDTH,
              display: "grid",
              gridTemplateColumns: `repeat(${col}, minmax(${COLUMN_WIDTH}px, auto))`,
              gridTemplateRows: gridTemplateRowsStr,
            }}
            className="container-col layout-grid-container"
          >
            {[...layoutCol]
              .sort(
                (a: AppPartType, b: AppPartType) =>
                  a.item_config_data.y - b.item_config_data.y,
              )
              .map((item: AppPartType) => {
                const { w, h, x, y, i } = item.item_config_data;
                const startCol = x - minX + 1;
                const startRow = y - colMinY + 1;
                const isTable = item.app_part_type === APP_PART_TYPES.TABLE;
                // An operator label is a column group on its own sitting
                // between the fields of its row, so line it up with their
                // controls. Every other label - headings, and the text
                // fragments contracts put between fields - is left alone.
                const alignToControl =
                  item.app_part_type === APP_PART_TYPES.LABEL &&
                  OPERATOR_LABEL_TEXTS.includes(getLabelPlainText(item)) &&
                  layoutCol.length === 1 &&
                  titledBands.some(
                    ([start, end]: number[]) => y < end && y + h > start,
                  );
                return (
                  <div
                    key={i}
                    className={`layout-grid-item ${isTable ? "is-table" : ""} ${
                      alignToControl ? "align-to-control" : ""
                    }`}
                    style={{
                      gridColumn: `${startCol} / span ${w}`,
                      gridRow:
                        isPDF && isTable
                          ? `${startRow} / auto`
                          : `${startRow} / span ${h}`,
                      width:
                        (item.label_text === "ステータス" ||
                          item.label_text === "カテゴリ" ||
                          item.field_code === "status_slicer" ||
                          item.field_code === "status_filter" ||
                          item.field_code === "カテゴリ") &&
                        isBusinessManuals
                          ? "668px"
                          : w * COLUMN_WIDTH - COLUMN_WIDTH,
                      marginLeft:
                        (item.label_text === "規程名" ||
                          item.name === "規程名") &&
                        targetXCenter
                          ? targetXCenter -
                            (item.item_config_data.x * COLUMN_WIDTH +
                              (w * COLUMN_WIDTH) / 2)
                          : undefined,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: 10,
                        alignItems: "center",
                        backgroundColor: "#f0f2f5",
                      }}
                    >
                      {((item.app_part_type !== APP_PART_TYPES.TABLE &&
                        item.app_part_type !== APP_PART_TYPES.SIGNATURE) ||
                        isPDF) && <Title item={item} />}
                    </div>
                    {renderField(item, selectionDataList, {
                      isDisabled,
                      permission,
                      onChange: onChange,
                      records: appRecord,
                      isPDF: isPDF || false,
                      allowSearchDate,
                      form,
                      appType,
                      appPartsFlat,
                      appId: String(appRecord?.app_id || ""),
                      recordId: String(appRecord?.id || ""),
                      handleImportTableRows,
                      onPrintPDF,
                      isPrinting: !!isPrinting,
                      isCreate,
                      appParts,
                      isQuickViewMode,
                    })}
                  </div>
                );
              })}
          </div>
        </span>
      );
    });
  };
  return (
    <Spin spinning={!!isLoading}>
      <Styles>
        {isPDF ? (
          <div>
            {newLayout.map((layoutRows, rowIndex) => (
              <div
                key={rowIndex}
                style={{
                  marginTop: layoutRows.marginTop * ROW_HEIGHT * 0.4,
                  display: "flex",
                }}
              >
                {renderCol(layoutRows)}
              </div>
            ))}
          </div>
        ) : (
          <BlockScrollInternal offSetHeight={isQuickViewMode ? 48 : 0}>
            <div
              className="layout-screen"
              style={{ marginBottom: 100, padding: 10 }}
            >
              {newLayout.map((layoutRows, rowIndex) => (
                <div
                  key={rowIndex}
                  style={{
                    marginTop: layoutRows.marginTop * ROW_HEIGHT,
                    display: "flex",
                  }}
                >
                  {renderCol(layoutRows)}
                </div>
              ))}
            </div>
          </BlockScrollInternal>
        )}
        {!isPDF && (
          <BusinessTripReportsCalculator
            form={form}
            appPartsFlat={appPartsFlat}
            appType={appType}
            isCreate={!!matchCreateBusinessTripReports}
          />
        )}
      </Styles>
    </Spin>
  );
};

export const LayoutAppForm: FC<ReceivedProps> = (props) => (
  <LayoutApp {...useLayoutApp(props)} />
);

export default LayoutAppForm;
